// Single source of truth for the flags this repo checks.
// name    — snake_case, epicenter_-prefixed, matches the gate name in the Statsig console
//           (dst_changes is a deliberate exception — see its entry below)
// default — served when the provider is unreachable/not ready; also what the local provider serves.
//           Release flags default false; only an operational switch that must survive an outage ON
//           may declare default true.
export const FLAGS = {
  EPICENTER_FLAGS_CANARY: {
    name: "epicenter_flags_canary",
    default: false,
    description:
      "Feature-flag system canary — surfaced by the /flags-debug screen; safe to flip in any tier",
  },

  EPICENTER_MENTOR_KYC_EXPORT: {
    name: "epicenter_mentor_kyc_export",
    default: false,
    description:
      "Mentor KYC CSV export access (GET /mentor/kyc/download-csv) — per-email gate, not a release flag",
  },

  EPICENTER_EDIT_ORDER_REVAMP: {
    name: "epicenter_edit_order_revamp",
    default: false,
    description:
      "Revamped Edit Order tab (SPRIN-2785): the EditOrderV2 tree on the existing EditProfile route; OFF serves the legacy EditOrder screen",
  },

  EPICENTER_MODERATOR_CSM_MERGE: {
    name: "epicenter_moderator_csm_merge",
    default: false,
    description:
      "SPRIN-3237: merge the Moderator role into CSM — cs_person gets Moderator's full permission set",
  },

  EPICENTER_MENTOR_TASKS_BULK: {
    name: "epicenter_mentor_tasks_bulk",
    default: false,
    description:
      "SPRIN-3232: Mentor Tasks bulk (CSV) upload option — same gate name as the EpicenterApi backend flag that processes it",
  },

  EPICENTER_MENTOR_RETRAINING_BULK: {
    name: "epicenter_mentor_retraining_bulk",
    default: false,
    description:
      "Class Retraining Data (CSV) upload option, type 14 — same gate name as the EpicenterApi backend flag that processes it",
  },

  EPICENTER_ADMIN_MAKEUP_CLASS: {
    name: "epicenter_admin_makeup_class",
    default: false,
    description:
      "SPRIN-3271: added the flex makeup enhancement FE — Assign Makeup Class  for flex classes",
  },

  EPICENTER_MENTOR_SELECT_ALL: {
    name: "epicenter_mentor_select_all",
    default: false,
    description:
      "SPRIN-3232: Active/Blacklisted Mentors select-all checkboxes in the Create mentor task popup",
  },

  EPICENTER_FREE_SCHEDULE_OCCUPIED_SLOTS: {
    name: "epicenter_free_schedule_occupied_slots",
    default: false,
    description: "Free Schedule shows occupied RBT slots with their batch details",
  },

  EPICENTER_LIVESTREAM_URL_VISIBILITY_TO_MENTORS: {
    name: "epicenter_livestream_url_visibility_to_mentors",
    default: false,
    description:
      "SPRIN-3280: View Class schedule — mentors see the Live Stream URL only from indiaStartTime to indiaStartTime + duration, '---' otherwise",
  },

  EPICENTER_LEARNING_SUMMARY: {
    name: "epicenter_learning_summary",
    default: false,
    description:
      "SPRIN-3263: Learning Summary page (#/learning-summary/:batchId) + GET /epicenter/student-brief — replaces the AI learning summary modal on Upcoming classes; FE only — the EpicenterApi backend gate is epicenter_student_brief (a different name), so switch the two flags together",
  },

  EPICENTER_CLASS_JOIN_CARD: {
    name: "epicenter_class_join_card",
    default: false,
    description:
      "Mentor dashboard Upcoming classes: the redesigned class card (Prepare / Teach / Wrap up, Critical tag, Class instructions join popup); OFF serves the existing SessionCard",
  },

  EPICENTER_TIMETABLE_POPUP: {
    name: "epicenter_timetable_popup",
    default: false,
    description:
      "Mentor timetable (desktop): the new popup for classes, demos, free slots and free schedules, with the Critical tag; OFF opens Syncfusion's existing popup — same gate name as the EpicenterApi flag that adds criticalTag to /mentorTimeTable",
  },

  EPICENTER_ORDER_AMOUNT_VALIDATION: {
    name: "epicenter_order_amount_validation",
    default: false,
    description:
      "SPRIN-3281 (BE: SPRIN-3278/TECHI-1034): block order submit when a subscription or installment amount is zero — same gate name as the EpicenterApi backend flag that enforces it",
  },

  EPICENTER_UPSELL_PREVIOUS_MENTOR: {
    name: "epicenter_upsell_previous_mentor",
    default: false,
    description:
      "SPRIN-3285: Upsell — per-course Mentor Preferences, pre-selecting the student's last assignable mentor in the same subject, sent as the demo mentor",
  },

  EPICENTER_EDIT_ORDER_REFUND_REMOVAL_GUARD: {
    name: "epicenter_edit_order_refund_removal_guard",
    default: false,
    description:
      "Edit Order: a student whose sub-order holds a refunded course cannot be deleted, attended or not — same gate name as the EpicenterApi backend flag that enforces it, so the button block and the save rule flip together",
  },

  EPICENTER_MENTOR_AI_HOMEWORK_STATS: {
    name: "epicenter_mentor_ai_homework_stats",
    default: false,
    description:
      "SPRIN-3261: AI homework split out on the mentor side — AI Homework section + pending AI homework popup on the My Students card, AI homework counts on the student profile. Same gate name as the EpicenterApi backend flag that sends the fields and serves GET /dashboard/homework/ai-pending",
  },

  EPICENTER_MENTOR_HOMEWORK_COUNTS_V2: {
    name: "epicenter_mentor_homework_counts_v2",
    default: false,
    description:
      "SPRIN-3261: corrected homework counts on /dashboard/student-stats — FE shows the new 'Last homework submitted' line on the My Students card. Same gate name as the EpicenterApi backend flag that computes the counts",
  },

  EPICENTER_AUS7_SHIFT: {
    name: "epicenter_aus7_shift",
    default: false,
    description:
      "SPRIN-3294: offer the AUS_7 work shift (08:30 AM - 01:30 PM IST, all days) in the user form — same gate name as the EpicenterApi backend flag that enforces it",
  },

  EPICENTER_IST_SHIFTS: {
    name: "epicenter_ist_shifts",
    default: false,
    description:
      "Offer the seven fixed-IST work shifts (6:30_11:30, 11:30_16:30, 17:30_22:30, 20:30_1:30, 12:30_4:30, 1:30_5:30, 3:30_7:30) in the user form — same gate name as the EpicenterApi backend flag that enforces it",
  },

  EPICENTER_MENTOR_RESOURCES_REGION_FILTER: {
    name: "epicenter_mentor_resources_region_filter",
    default: false,
    description:
      "SPEIN-3308: Region filter (All Countries / Australia) on mentor Resources visual aids — same gate name as the EpicenterApi backend flag, so the radio row and the region-filtered reads flip together",
  },
  // Intentionally unprefixed: ONE Statsig gate shared with Sandbox and
  // StudentPortal, so a single flip switches DST-aware labels on in all three
  // apps at once. They render the same classes — showing one abbreviation here
  // and a different one there would be worse than shipping neither.
  DST_CHANGES: {
    name: "dst_changes",
    default: false,
    description:
      "SPRIN-3287: DST-aware timezone abbreviations — derive the abbreviation from the IANA zone instead of the hardcoded label in timeZoneList",
  },

  EPICENTER_AUTO_STITCH_T14: {
    name: "epicenter_auto_stitch_t14",
    default: false,
    description:
      "Batch Stitching Automation M2+M3 FE half — same gate name as the EpicenterApi backend flag "
      + "(one Statsig flip covers both): the Loop screen's auto-stitch checkbox + automate payload, "
      + "and the T-14 wording in the Add Batch stitch section; OFF is today's screens byte-for-byte",
  },

  EPICENTER_BATCH_FLOW_REVAMP: {
    name: "epicenter_batch_flow_revamp",
    default: false,
    description:
      "Add Batch + Edit Batch revamp (SPRIN-3114): AddBatchV2/EditBatchV2 trees and the Home batch-column '+' entry; OFF serves the legacy flows",
  },

  EPICENTER_SPR_V2: {
    name: "epicenter_spr_v2",
    default: false,
    description:
      "SPRIN-3315: SPR v2 — reviewable Student Progress Report: the SprStatusBlock entry on the mentor Students tab and the LM student profile",
  },

  EPICENTER_SPR_V2_SEND: {
    name: "epicenter_spr_v2_send",
    default: false,
    description:
      "SPR v2 milestone 2, on top of epicenter_spr_v2: the summary, Add topic, Send report, the entry block's SPR History, the /spr-sent preview and the parent's public /spr/sent link. The parent page is evaluated as `anonymous`, so the gate must pass everyone in a tier for parents to see it",
  },

  EPICENTER_CSM_PAYMENT_DETAILS_VIEW: {
    name: "epicenter_csm_payment_details_view",
    default: false,
    description:
      "SPRIN-3301: CSM (cs_person, plus the legacy Moderator role string) can open the student profile's Payment Details tab; EDIT PAYMENT inside stays admin-only",
  },

  // SPRIN-3319 — AUS mentor shift windows. Same gate name as EpicenterApi's, and the two MUST
  // flip together: this app builds the auto-add grid from its own copy of the literals and POSTs
  // it, so a disagreement writes the wrong window straight into the DB.
  //
  // Scope is AUS_1 and AUS_6. AUS_7 shares the market but deliberately does not move. The UK and
  // US shifts have gates of their own below.
  //
  // PERMANENT TWO-STATE GATE, never retired (see README, "Seasonal gates"): flipped ON at the
  // Australian spring-forward and OFF at the autumn transition, twice a year. `default: false`
  // means "AEST season", not "unreleased" — which is why the screens that POST a window hold
  // their submit until the provider has answered instead of trusting the default.
  //
  // Do NOT ramp by percentage. The backend evaluates this on one fixed system identity for that
  // reason; this app can only evaluate it on the signed-in session, so a ramp would hand two
  // mentors different windows for the same shift.
  EPICENTER_MENTOR_SHIFT_DST_AUS: {
    name: "epicenter_mentor_shift_dst_aus",
    default: false,
    description:
      "AUS mentor shift windows. ON (~Oct-Apr, Australia on AEDT): AUS1 weekdays 10:30 AM-03:30 PM and weekend band 05:30 AM-03:30 PM, AUS_6 07:30 AM-01:30 PM IST. OFF (~Apr-Oct, AEST): AUS1 11:30 AM-04:30 PM and 06:30 AM-04:30 PM, AUS_6 08:30 AM-02:30 PM. AUS_7 is not in scope and does not move",
  },

  // SPRIN-3319 — UK and US mentor shift windows. Same rules as the AUS gate above: same gate names
  // as EpicenterApi's and flipped together with it, permanent two-state gates, never ramped by
  // percentage. ON moves the region's windows an hour LATER in IST (the market has fallen back);
  // `default: false` means "summer season", not "unreleased".
  EPICENTER_MENTOR_SHIFT_DST_UK: {
    name: "epicenter_mentor_shift_dst_uk",
    default: false,
    description:
      "UK mentor shift windows. ON (~Oct-Mar, UK on GMT): UK_2 09:30 PM-02:30 AM, UK_6 09:30 PM-03:30 AM IST. OFF (~Mar-Oct, BST): UK_2 08:30 PM-01:30 AM, UK_6 08:30 PM-02:30 AM",
  },
  EPICENTER_MENTOR_SHIFT_DST_US: {
    name: "epicenter_mentor_shift_dst_us",
    default: false,
    description:
      "US mentor shift windows. ON (~Nov-Mar, US on standard time): US_1 Tue-Fri 03:30 AM-08:30 AM and Sat/Sun nights 08:30 PM-02:30 AM, US_2 03:30 AM-08:30 AM, US_6 03:30 AM-09:30 AM IST. OFF (~Mar-Nov, daylight): US_1 02:30 AM-07:30 AM and 07:30 PM-01:30 AM, US_2 02:30 AM-07:30 AM, US_6 02:30 AM-08:30 AM",
  },

  EPICENTER_VIEW_SCHEDULE_MENTOR_SLOTS: {
    name: "epicenter_view_schedule_mentor_slots",
    default: false,
    description:
      "View Schedule shows the batch mentor's free and on-request slot counts for the next 7 days — same gate name as the EpicenterApi backend flag on GET /mentor/:mentorId/slot-summary",
  },

  EPICENTER_DISCOUNT_INACTIVE_SUBSCRIPTION: {
    name: "epicenter_discount_inactive_subscription",
    default: false,
    description:
      "Add Discount on an inactive gateway subscription (SPRIN-3321): blocked while a gateway change is pending, 0 installments when nothing is left, the new link shown — same gate name as the EpicenterApi flag on PUT /payment/modify",
  },

  EPICENTER_DISCOUNT_PAYGLOCAL: {
    name: "epicenter_discount_payglocal",
    default: false,
    description:
      "Add Discount on PayGlocal subscriptions (SPRIN-3321): lifts the PayGlocal block only when epicenter_discount_inactive_subscription is also on — same gate name as the EpicenterApi flag on PUT /payment/modify",
  },

  EPICENTER_VENDOR_SUBSCRIPTION_INTERVALS: {
    name: "epicenter_vendor_subscription_intervals",
    default: false,
    description:
      "Change Payment vendor (SPRIN-3321): Subscription Period Interval becomes a dropdown filled from GET /payment/intervals for the selected gateway, and Submit stays disabled until a gateway is picked; OFF keeps the read-only interval",
  },

  EPICENTER_MENTOR_REACTIVATION: {
    name: "epicenter_mentor_reactivation",
    default: false,
    description:
      "Users page re-enables exited mentors (SPRIN-3297): an Exited option in the IsActive filter and an Enable form that restores login, shift and slots in one save — same gate name as the EpicenterApi flag on GET/POST /mentor-exit/…/reactivation",
  },

  EPICENTER_MENTOR_SHIFT_MANAGEMENT: {
    name: "epicenter_mentor_shift_management",
    default: false,
    description:
      "Mentor shift revamp M1: admin-only read-only Shift Management page (/mentor-shifts) + its sidebar entry, and the shift details line in Users → Edit mentor",
  },

  // Mentor shift revamp (L3) — the M2 create / edit / disable shift screens. Same gate name as
  // EpicenterApi's, which gates the write APIs behind them. Flip for everyone, never ramp:
  // validators and the worker must agree for one mentor. The backend's V2_SHADOW / V2_LIVE gates
  // have no FE counterpart; screens follow `shift.source` from the API instead.
  EPICENTER_MENTOR_SHIFT_ADMIN: {
    name: "epicenter_mentor_shift_admin",
    default: false,
    description:
      "Mentor shift revamp M2: same gate name as the EpicenterApi backend flag on the shift write APIs (the screens are behind epicenter_mentor_shift_add_edit_ui)",
  },

  EPICENTER_MENTOR_SHIFT_ADD_EDIT_UI: {
    name: "epicenter_mentor_shift_add_edit_ui",
    default: false,
    description:
      "Mentor shift revamp M2: the Add Shift button, the Edit / Enable column and the Add / Edit shift popup on the Shift Management page",
  },

  EPICENTER_MENTOR_SCRATCH_PROJECT_SUMMARY: {
    name: "epicenter_mentor_scratch_project_summary",
    default: false,
    description:
      "AI-generated Scratch project summary section in the mentor AI Learning Summary modal — same gate name as the EpicenterApi backend flag that computes and sends aiSummary, so the FE can also hide the section on its own; also gates the project summary card on the Learning Summary page",
  },

  EPICENTER_DEMO_MENTOR_RECOMMENDATION: {
    name: "epicenter_demo_mentor_recommendation",
    default: false,
    description:
      "MX-3: demo mentor recommendation — Add Lead and Reschedule mount DemoSlotMentorSection (suggested mentor cards + Choose my own mentor) and book through the /v2 endpoints; Substitute mentor reads GET /mentors/available/:slotId/v2. OFF serves today's v1 screens and endpoints",
  },

  // Intentionally unprefixed: ONE Statsig gate shared with cy-sso (which gates the
  // impersonate-code and impersonate-exchange routes) and the StudentApp (its ImpersonationLogin
  // screen), so a single flip turns the whole app-impersonation feature on or off. Plain on/off
  // per tier; the page is public, so a logged-out visitor evaluates as `anonymous`.
  CY_APP_IMPERSONATION_LOGIN: {
    name: "cy_app_impersonation_login",
    default: false,
    description:
      "App impersonation: the #/impersonate-app page (generate a deep link that opens the Codeyoung app as a parent). OFF renders Access Denied; same gate name as cy-sso's and the StudentApp's",
  },

  EPICENTER_FIRST_CLASS_CANCEL_SHIFT_START: {
    name: "epicenter_first_class_cancel_shift_start",
    default: false,
    description:
      "Mentor can cancel the first class of a batch from the edit-class modal (action id 6); the batch start date moves to the next class — same gate name as the EpicenterApi flag on the mentor-edit policy and submit routes",
  },
};

const byName = Object.values(FLAGS).reduce(
  (acc, flag) => ({ ...acc, [flag.name]: flag }),
  {},
);

export const defaultFor = (flagName) => byName[flagName]?.default ?? false;
